/** Sentinel work queue, everything that needs attention right now in one place. */
import { useState, useEffect } from 'react';
import { api, Spinner, Badge, Hint, dateShort, PRIORITY_TONE } from './ui';

const daysUntil = (d) => (d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null);

export default function SentinelQueue({ onOpenCase }) {
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let on = true;
    api('/queue').then((j) => on && setQ(j.queue)).catch((e) => on && setErr(e.message)).finally(() => on && setLoading(false));
    return () => { on = false; };
  }, []);

  if (loading) return <Spinner label="Loading your queue…" />;
  if (err) return <div className="p-6 text-sm text-red-600">{err}</div>;

  const Row = ({ c, right }) => (
    <button onClick={() => onOpenCase(c.id)} className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors">
      <span className="font-mono text-xs text-slate-400">{c.case_number}</span>
      <span className="text-sm text-slate-700 flex-1 truncate">{c.allegation_summary || '(no summary)'}</span>
      {c.priority && <Badge tone={PRIORITY_TONE[c.priority] || 'slate'}>{c.priority}</Badge>}
      {right}
    </button>
  );

  const Section = ({ title, hint, items, empty, render }) => (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center mb-3">
        <h2 className="font-semibold text-slate-800">{title}</h2>
        <Hint text={hint} />
        <span className="ml-auto text-xs font-semibold text-slate-400">{items.length}</span>
      </div>
      {items.length === 0 ? <p className="text-sm text-slate-400 py-2">{empty}</p> : <div className="space-y-2">{items.map(render)}</div>}
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <Section
        title="Needs your decision"
        hint="New referrals waiting to be triaged into investigate, monitor, or not a case."
        items={q.needs_triage}
        empty="Nothing waiting. Nicely done."
        render={(c) => <Row key={c.id} c={c} right={<span className="text-xs text-slate-400">{dateShort(c.created_at)}</span>} />}
      />
      <Section
        title="Deadlines coming due"
        hint="Cases with an overpayment whose 60 day report and return deadline is within 30 days, or already past."
        items={q.due_soon}
        empty="No deadlines in the next 30 days."
        render={(c) => {
          const d = daysUntil(c.due);
          const tone = d == null ? 'slate' : d <= 7 ? 'red' : d <= 21 ? 'amber' : 'slate';
          return <Row key={c.id} c={c} right={<Badge tone={tone}>{d < 0 ? `${Math.abs(d)}d over` : `${d}d left`}</Badge>} />;
        }}
      />
      <Section
        title="Cases that have gone quiet"
        hint="Open investigations with no activity in the last 7 days, so nothing slips."
        items={q.idle}
        empty="Every open case has recent activity."
        render={(c) => <Row key={c.id} c={c} right={<span className="text-xs text-slate-400">quiet since {dateShort(c.last_activity)}</span>} />}
      />
    </div>
  );
}
